import {GetPresignedSignatureUrl} from 'api/media/queries';
import {observable, action, makeObservable} from 'mobx';
import * as Sentry from '@sentry/react';

import {
  getChecklistFromLocalStorage,
  saveChecklistToLocalStorage,
  clearChecklistFromLocalStorage
} from 'utils/checklist-utils';

class ChecklistStore {
  @observable checklistId;
  @observable checklist = {};
  @observable isChecklistSubmitted = false;
  @observable isChecklistBeingSubmitted = false;
  @observable showErrorModal = false;
  @observable showErrorSubmittingModal = false;
  @observable showChecklistsDisabledModal = false;

  setNotification = notification => {
    this.notification = notification;
  };

  @action
  setChecklistId = checklistId => (this.checklistId = checklistId);

  @action
  setChecklist = checklist => (this.checklist = checklist);

  @action
  setIsChecklistSubmitted = isChecklistSubmitted => (this.isChecklistSubmitted = isChecklistSubmitted);

  @action
  openErrorModal = () => (this.showErrorModal = true);

  @action
  closeErrorModal = () => (this.showErrorModal = false);

  @action
  openErrorSubmittingModal = () => (this.showErrorSubmittingModal = true);

  @action
  closeErrorSubmittingModal = () => (this.showErrorSubmittingModal = false);

  @action
  openChecklistsDisabledModal = () => (this.showChecklistsDisabledModal = true);

  @action
  closeChecklistsDisabledModal = () => (this.showChecklistsDisabledModal = false);

  @action
  setIsChecklistBeingSubmitted = isChecklistBeingSubmitted =>
    (this.isChecklistBeingSubmitted = isChecklistBeingSubmitted);

  @action
  updateChecklist = (stepId, infos = {}) => {
    const {checked, unchecked, feedbackText, mediaUrl, optionId} = infos;
    const checklist = getChecklistFromLocalStorage(this.checklistId);
    if (
      checklist[stepId]?.checkEscalated &&
      (checked !== checklist[stepId].checked ||
        unchecked !== checklist[stepId].unchecked ||
        optionId !== checklist[stepId].optionId ||
        feedbackText !== checklist[stepId].feedbackText ||
        mediaUrl !== checklist[stepId].mediaUrl)
    ) {
      checklist[stepId].checkEscalated = false;
    }

    if (checklist[stepId]?.escalationAttemptsIds && infos.escalationAttemptsIds) {
      infos.escalationAttemptsIds = [...checklist[stepId].escalationAttemptsIds, ...infos.escalationAttemptsIds];
      //Generate the HASH of the current state and store it in array
    }

    // Remove answer if the same value is clicked again - OLD CHECKLIST
    const existingCheckedValue = checklist[stepId] && checklist[stepId].checked;
    const existingUncheckedValue = checklist[stepId] && checklist[stepId].unchecked;

    if (existingCheckedValue === checked && existingUncheckedValue === unchecked) {
      infos.checked = false;
      infos.unchecked = false;
    }

    // Remove answer if the same value is clicked again - NEW CHECKLIST
    if (optionId === checklist[stepId]?.optionId) {
      infos.optionId = null;
    }

    checklist[stepId] = {...checklist[stepId], ...infos};

    const isEmptyChecklist = Object.keys(checklist).length === 0;

    isEmptyChecklist
      ? clearChecklistFromLocalStorage(this.checklistId)
      : saveChecklistToLocalStorage(this.checklistId, checklist);
    this.setChecklist(checklist);
  };

  @action
  resetChecklist = () => {
    if (this.checklistId) {
      this.updateChecklist({});
      clearChecklistFromLocalStorage(this.checklistId);
      this.setChecklistId();
      this.setIsChecklistSubmitted(false);
      this.setIsChecklistBeingSubmitted(false);
    }
  };

  @action
  submitChecklist = async (
    fullName,
    signatureSvg,
    submitChecklistAnswerMutation,
    nextButton,
    instruction,
    extraFieldValue = ''
  ) => {
    const {
      instructionPage: {
        currentInstruction: {id: checklistId, steps},
        currentGuideVersionId
      },
      platform: {hasChecklistsSignatureEnabled}
    } = this.store;
    const signatureKey = '';

    this.setIsChecklistBeingSubmitted(true);

    const items = steps.map(step => {
      const foundCheck = this.checklist[step.id];

      return foundCheck
        ? {
            stepId: step.id,
            value: this.checklist[step.id].checked ? true : this.checklist[step.id].unchecked ? false : null,
            checklistTemplateOptionId: this.checklist[step.id].optionId || null,
            feedback: this.checklist[step.id].feedbackText || '',
            mediaUrl: this.checklist[step.id].mediaUrl || null,
            escalationAttemptsIds: this.checklist[step.id].escalationAttemptsIds || []
          }
        : {
            stepId: step.id,
            value: null,
            feedback: '',
            mediaUrl: null,
            escalationAttemptsIds: []
          };
    });

    const checklistAnswer = {
      instructionId: checklistId,
      guideVersionId: currentGuideVersionId,
      user: fullName,
      items,
      signatureKey
    };
    if (extraFieldValue.trim()) {
      checklistAnswer.extraFieldValue = extraFieldValue.trim();
    }
    try {
      if (hasChecklistsSignatureEnabled && instruction.requireSignature) {
        checklistAnswer.signatureKey = await this.uploadSignature(signatureSvg);
      }
      const result = await submitChecklistAnswerMutation({checklistAnswer});

      if (!result.data.submitChecklistAnswer.result) {
        throw new Error('Checklist answers not submitted');
      }

      this.setIsChecklistSubmitted(true);

      if (nextButton) {
        nextButton.click();
      }

      clearChecklistFromLocalStorage(checklistId);
      this.setChecklist({});
    } catch (error) {
      Sentry.captureException(error);
      this.openErrorSubmittingModal();
    } finally {
      this.setIsChecklistBeingSubmitted(false);
    }
  };

  setStore = store => (this.store = store);

  uploadSignature = async signatureSvg => {
    const {data} = await this.apolloClient.query({
      fetchPolicy: 'network-only',
      query: GetPresignedSignatureUrl
    });
    const signatureUploadUrl = data?.getPresignedSignatureUrl?.url;
    const key = data?.getPresignedSignatureUrl?.key;
    const response = await fetch(signatureUploadUrl, {
      method: 'PUT',
      body: signatureSvg,
      headers: {
        'Content-Type': 'image/svg+xml'
      }
    });
    if (!response.ok) {
      throw new Error(`Error uploading signature: ${response}`);
    }
    return key;
  };

  feedbackForStep = stepId => this.checklist[stepId]?.feedbackText;
  mediaUrlForStep = stepId => this.checklist[stepId]?.mediaUrl;

  constructor(apolloClient) {
    this.apolloClient = apolloClient;
    makeObservable(this);
  }
}

export default ChecklistStore;
